import { useEffect, useRef, useState } from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { TFunction } from 'i18next';
import * as SG from 'styles';

import { ORDER_INTERNAL_NOTE_MAX_LENGTH, ORDER_NOTE_MAX_LENGTH } from 'constants/orderNotes';

import { Option } from 'types';

import {
  AddressDetailsPanel,
  CODE_SELECT_MENU_MAX_WIDTH,
  CollapsibleField,
  CustomerDetailsPanel,
  NameBox,
  codeSelectComponents,
  formatCodeOptionLabel,
} from 'components/OrderAddressBlocks';
import type { SelectProps } from 'components/SelectWithControl/SelectWithControl';

import { FieldType, useOrder } from './Order.hooks';
import * as S from './Order.styles';
import { Actions } from './components/Actions';
import { Error } from './components/Error';
import { NotFound } from './components/NotFound';
import { OrderCharts } from './components/OrderCharts';
import { OrderCustomerField } from './components/OrderCustomerField';
import { OrderHeader } from './components/OrderHeader';
import { OrderNoteField } from './components/OrderNoteField';
import { ProductTable } from './components/ProductTable';
import { LOADING_SKELETON_ROWS } from './components/ProductTable/ProductTable';
import { StyleRowSkeleton } from './components/ProductTable/components/StyleRowSkeleton';
import { FormField } from './form.data';

/** Shipping / Goods selects: code in the code column, full labels in the list. */
const ADDRESS_SELECT_OPTIONS = {
  isSearchable: false,
  hideEmptyOption: true,
  formatOptionLabel: formatCodeOptionLabel,
  menuMaxWidth: CODE_SELECT_MENU_MAX_WIDTH,
};

function renderFieldControl(
  item: FormField,
  control: Control<FieldValues>,
  isSend: boolean,
  t: TFunction,
  onSelect: (fieldName: string, value: string) => void,
  SelectComponent: typeof S.Select | typeof S.CompactSelect,
  options?: Pick<
    SelectProps,
    'isSearchable' | 'hideEmptyOption' | 'formatOptionLabel' | 'components' | 'menuMaxWidth'
  >
) {
  const label = t(item.label) as string;
  if (item.kind === 'readonly') {
    return (
      <S.ReadOnlyField>
        <S.ReadOnlyLabel>{label}</S.ReadOnlyLabel>
        <S.ReadOnlyValue>{item.readonlyValue ?? '—'}</S.ReadOnlyValue>
      </S.ReadOnlyField>
    );
  }
  return (
    <SelectComponent
      name={item.name}
      options={item.options}
      control={control}
      label={label}
      disabled={item.disabled || isSend}
      motive="filter"
      data-testid="select-form"
      required={item.required}
      defaultValue={item.defaultValue}
      onSelect={(e: Option) => onSelect(item.name, String(e.value))}
      isSearchable={options?.isSearchable}
      hideEmptyOption={options?.hideEmptyOption}
      emptyOptionLabel={t('common.selectPlaceholder')}
      formatOptionLabel={options?.formatOptionLabel}
      components={options?.components}
      menuMaxWidth={options?.menuMaxWidth}
    />
  );
}

export function Order() {
  const {
    control,
    formFields,
    register,
    updateOrderData,
    debouncedSave,
    order,
    draftCustomerCode,
    setDraftCustomerCode,
    hasErrors,
    showErrors,
    isSend,
    isBasicInfoLoading,
    isOrderNotFound,
    internalNote,
    orderNote,
    customerDetails,
    customerInfo,
    orderHeader,
    customerSearchZone,
    selectedShippingAddressDto,
    selectedGoodsDestinationDto,
    persistCustomerOnBlur,
    notesFormKey,
    missingSendFields,
  } = useOrder();

  const { t } = useTranslation();

  // Show a loading toast while order data is being fetched
  const loadingToastRef = useRef<string | null>(null);
  useEffect(() => {
    if (isBasicInfoLoading && !loadingToastRef.current) {
      loadingToastRef.current = toast.loading(t('order.loading'), {
        id: 'order-loading',
      });
    } else if (!isBasicInfoLoading && loadingToastRef.current) {
      toast.dismiss(loadingToastRef.current);
      loadingToastRef.current = null;
    }
    return () => {
      if (loadingToastRef.current) {
        toast.dismiss(loadingToastRef.current);
        loadingToastRef.current = null;
      }
    };
  }, [isBasicInfoLoading, t]);

  // Defer ProductTable mount to prioritise the form fields paint first.
  const [showTable, setShowTable] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setShowTable(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const onSelectField = (fieldName: string, value: string) => {
    updateOrderData(value, fieldName as FieldType);
  };

  // The order was deleted or never existed (404) — show a friendly page with a
  // way back to the list instead of an empty form.
  if (isOrderNotFound) {
    return (
      <SG.Inner>
        <NotFound />
      </SG.Inner>
    );
  }

  return (
    <SG.Inner>
      <OrderHeader />
      <OrderCharts />
      {/* <Loader isLoading={isBasicInfoLoading}> */}
      <S.FormSection aria-label={t('order.form')}>
        <S.PrimaryStack>
          <CollapsibleField
            data-testid="customer-collapsible"
            field={(isOpen) => (
              <OrderCustomerField
                control={control}
                readOnly={formFields.customer.kind === 'readonly'}
                readOnlyDisplay={formFields.customer.readonlyValue ?? '—'}
                searchZone={customerSearchZone}
                customerInfo={customerInfo}
                savedCustomerCode={order?.customerCode}
                draftCustomerCode={draftCustomerCode}
                onDraftCustomerCode={setDraftCustomerCode}
                onBlur={persistCustomerOnBlur}
                expanded={isOpen}
              />
            )}
            name={
              <NameBox data-testid="customer-name">
                {customerDetails?.displayName || customerInfo?.companyName || '—'}
              </NameBox>
            }
            details={
              <CustomerDetailsPanel
                details={customerDetails}
                buyerName={orderHeader?.buyerName}
                buyerEmail={orderHeader?.buyerEmail}
              />
            }
          />

          <CollapsibleField
            data-testid="shipping-collapsible"
            field={(isOpen) =>
              renderFieldControl(
                formFields.shipping,
                control,
                isSend,
                t,
                onSelectField,
                S.Select,
                {
                  ...ADDRESS_SELECT_OPTIONS,
                  components: codeSelectComponents(isOpen),
                }
              )
            }
            name={
              <NameBox data-testid="shipping-name">
                {selectedShippingAddressDto?.displayName || '—'}
              </NameBox>
            }
            details={
              <AddressDetailsPanel
                address={selectedShippingAddressDto}
                emptyMessage={t('address.selectShippingHint')}
                withShippingMethod
                data-testid="shipping-address-details"
              />
            }
          />

          <CollapsibleField
            data-testid="goods-collapsible"
            field={(isOpen) =>
              renderFieldControl(
                formFields.goods,
                control,
                isSend,
                t,
                onSelectField,
                S.Select,
                {
                  ...ADDRESS_SELECT_OPTIONS,
                  components: codeSelectComponents(isOpen),
                }
              )
            }
            name={
              <NameBox data-testid="goods-name">
                {selectedGoodsDestinationDto?.displayName || '—'}
              </NameBox>
            }
            details={
              <AddressDetailsPanel
                address={selectedGoodsDestinationDto}
                emptyMessage={t('address.selectGoodsHint')}
                data-testid="goods-destination-details"
              />
            }
          />
        </S.PrimaryStack>
      </S.FormSection>

      <form>
        <S.NotesStack>
          <CollapsibleField
            data-testid="internal-notes-collapsible"
            field={(isOpen) =>
              renderFieldControl(
                formFields.orderTakenOn,
                control,
                isSend,
                t,
                onSelectField,
                S.CompactSelect,
                { components: codeSelectComponents(isOpen) }
              )
            }
            name={(isOpen) => (
              <OrderNoteField
                key={`internalNote-${notesFormKey}`}
                register={register}
                name="internalNote"
                value={internalNote}
                label={t('order.internalNotes')}
                maxLength={ORDER_INTERNAL_NOTE_MAX_LENGTH}
                disabled={isSend}
                onChange={(value) => debouncedSave(value, 'internalNote')}
                expanded={isOpen}
                data-testid="internal-note-field"
              />
            )}
          />
          <CollapsibleField
            data-testid="order-notes-collapsible"
            toggleOnLabel={(isOpen) => !isOpen}
            field={(isOpen) => (
              <OrderNoteField
                key={`notes-${notesFormKey}`}
                register={register}
                name="notes"
                value={orderNote}
                label={t('order.notes')}
                maxLength={ORDER_NOTE_MAX_LENGTH}
                disabled={isSend}
                onChange={(value) => debouncedSave(value, 'note')}
                expanded
                hidden={!isOpen}
                data-testid="order-note-field"
              />
            )}
          />
        </S.NotesStack>
      </form>

      <S.CompactRow>
        <S.CompactCellReadonly>
          <S.ReadOnlyLabel>{t('order.seasonCollection')}</S.ReadOnlyLabel>
          <S.SeasonCollectionBox data-testid="season-collection">
            {formFields.seasonCollection}
          </S.SeasonCollectionBox>
        </S.CompactCellReadonly>
        <S.CompactCellSelect>
          {renderFieldControl(
            formFields.deliveryCode,
            control,
            isSend,
            t,
            onSelectField,
            S.CompactSelect
          )}
        </S.CompactCellSelect>
      </S.CompactRow>

      {showTable ? (
        <ProductTable />
      ) : (
        <S.ProductTableFallback aria-busy="true">
          {Array.from({ length: LOADING_SKELETON_ROWS }, (_, i) => (
            <StyleRowSkeleton key={i} />
          ))}
        </S.ProductTableFallback>
      )}

      {showErrors && (hasErrors || missingSendFields.length > 0) ? (
        <Error missingFields={missingSendFields} />
      ) : undefined}

      <Actions />
      {/* </Loader> */}
    </SG.Inner>
  );
}
